/** @jsx jsx */
import { jsx, ClassNames } from '@emotion/react'
import { css } from '@emotion/react/macro'
import ReactModal from 'react-modal'

import useModal from '../../../../lib/hooks/useModal'
import { BP_SMALL_DOWN } from '../../../../lib/styles/breakpoints'
import { errorTextColor } from '../../../../lib/styles/colors'
import { twentyFourDpShadowCss } from '../../../../lib/styles/shadows'

export enum ModalSize {
  SM = 'small',
  MD = 'medium',
  LG = 'large',
  FULL = 'full'
}

export type ModalProps = {
  /** Display as a full page on mobile */
  allowMobilePage?: boolean
  /** Children components to render */
  children: React.ReactNode
  /** Indicates the modal should be highlighted with a DANGER border */
  isDanger?: boolean
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when user triggers a close event (eg. button click or ESC key) */
  onClose: () => void
  /** Event callback when user submits the form. By passing in this prop the inner conten will render as a form. */
  onSubmit?: (e: React.FormEvent) => void
  /** Size of modal */
  size?: ModalSize
}

const defaultProps: Pick<ModalProps, 'allowMobilePage' | 'isDanger' | 'onSubmit' | 'size'> = {
  allowMobilePage: undefined,
  isDanger: false,
  onSubmit: undefined,
  size: ModalSize.SM
}

const baseContentCss = css`
  position: absolute;
  top: 40px;
  border: 1px solid rgb(204, 204, 204);
  background: rgb(255, 255, 255);
  overflow: initial;
  border-radius: 4px;
  outline: none;
  padding: 30px;
  min-height: 200px;
  z-index: 999;
  ${twentyFourDpShadowCss}
`
const innerContent = css`
  display: grid;
  grid-auto-rows: max-content;
  grid-template-rows: 2rem 1fr 3rem;
  grid-template-columns: 1fr 1fr;
`
const mobileInnerContent = css`
  grid-template-rows: 56px 1fr 3rem;
`
const baseOverlayCss = css`
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background: rgba(0, 0, 0, 0.32);
  z-index: 9990;
  display: flex;
  justify-content: center;
  overflow: auto;
`
const mobileContentCss = css`
  @media (${BP_SMALL_DOWN}) {
    position: fixed;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    max-height: 100vh;
    padding: 0;
  }
`

const getMediaFullWidthCss = (breakPoint: string) => css`
  @media (max-width: ${breakPoint}) {
    width: unset;
    left: 20px;
    right: 20px;
  }
`
const getSizeCss = (size: ModalSize | undefined) => {
  switch (size) {
    case ModalSize.SM:
      return css`
        width: 400px;
        ${getMediaFullWidthCss('478px')}
      `
    case ModalSize.MD:
      return css`
        width: 500px;
        ${getMediaFullWidthCss('578px')}
      `
    case ModalSize.LG:
      return css`
        width: 700px;
        ${getMediaFullWidthCss('778px')}
      `
    case ModalSize.FULL:
    default:
      return null
  }
}
const dangerBorderCss = css`
  border-top: 4px ${errorTextColor} solid;
`
const aria = {
  labelledby: 'modal_heading',
  describedby: 'modal_body'
}

export const Modal = ({
  allowMobilePage,
  children,
  isDanger,
  isOpen,
  onClose,
  onSubmit,
  size
}: ModalProps) => {
  const innerCssSets = [innerContent, allowMobilePage && mobileInnerContent]
  const inner = onSubmit ? (
    <form css={innerCssSets} onSubmit={onSubmit}>
      {children}
    </form>
  ) : (
    <div css={innerContent}>{children}</div>
  )
  return (
    <ClassNames>
      {({ css: classNameCss }) => (
        <ReactModal
          aria={aria}
          className={classNameCss`
            ${baseContentCss}
            ${getSizeCss(size)}
            ${allowMobilePage ? mobileContentCss : ''}
            ${isDanger ? dangerBorderCss : ''}
          `}
          closeTimeoutMS={300}
          isOpen={isOpen}
          onRequestClose={onClose}
          overlayClassName={classNameCss`${baseOverlayCss}`}
        >
          {inner}
        </ReactModal>
      )}
    </ClassNames>
  )
}

Modal.defaultProps = defaultProps

type ModalApolloProps = {
  /** Display as a full page on mobile */
  allowMobilePage?: boolean
  /** Children components to render */
  children: React.ReactNode
  /** Indicates the modal should be highlighted with a DANGER border */
  isDanger?: boolean
  /** Determines visibility of modal */
  isOpen: boolean
  /** Event callback when user submits the form. By passing in this prop the inner conten will render as a form. */
  onSubmit?: (e: React.FormEvent) => void
  /** Size of modal */
  size?: ModalSize
}

const ModalWrapper = ({
  allowMobilePage,
  children,
  isDanger,
  isOpen,
  onSubmit,
  size
}: ModalApolloProps) => {
  const { hideModal } = useModal()
  const onClose = () => hideModal()

  return (
    <Modal
      allowMobilePage={allowMobilePage}
      isDanger={isDanger}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      size={size}
    >
      {children}
    </Modal>
  )
}

ModalWrapper.defaultProps = defaultProps

export default ModalWrapper
